import { useCallback, useRef } from "react";
import { AgGridReact } from "ag-grid-react";
import Actionbar from "src/components/common/actionbar/Authactionbar";
import { Message } from "src/components/common/form/Message";
import { useMouseContent } from "src/utils/mouse/mouseContent";
import { TQC2080_R01Dto } from "./Dto_TQC2080_R01";
import { useTQC2080_R01Store } from "./Store_TQC2080_R01";
import { Content_TQC2080_R01 } from "./Content_TQC2080_R01";
import { SearchSection_TQC2080_R01 } from "./SearchSection_TQC2080_R01";
import { DateList_TQC2080_R01 } from "./DateList_TQC2080_R01";
import { Table_TQC2080_R01 } from "./Table_TQC2080_R01";
import { Chart_TQC2080_R01 } from "./Chart_TQC2080_R01";

const DATE_LIST_RESIZER = {
	code: "TQC2080_R01",
	type: "width" as const,
	storageKey: "TQC2080_R01-datelist-split-width",
	min: 8,
	max: 45,
	default: 14,
};

/** WMS2010_R01 Table과 동일한 탭 버튼 스타일 */
const MAIN_TABS = [
	{ id: "journal" as const, label: "일지등록" },
	{ id: "chart" as const, label: "차트" },
];

export const Container_TQC2080_R01 = () => {
	const gridRef = useRef<AgGridReact<TQC2080_R01Dto>>(null);
	const journalSplitContainerRef = useRef<HTMLDivElement>(null);
	const journalTableRef = useRef<HTMLDivElement>(null);
	const isJournalResizing = useRef(false);

	const { splitValue: dateListSplitPct, handleMouseDown: handleJournalSplitMouseDown } =
		useMouseContent({
			resizer: DATE_LIST_RESIZER,
			isResizing: isJournalResizing,
			containerRef: journalSplitContainerRef,
			infoRef: journalTableRef,
		});

	const { activeTab, chartData, TQC2080_R01Filter } = useTQC2080_R01Store();

	const {
		fetchData,
		handleCreateClick,
		openDateRowDetail,
		handleTabClick,
		handleInfoDateChange,
		handleSaveClick,
		handleDeleteClick,
		handleExcelClick,
		handlePdfClick,
		handleChartPdfClick,
		initContainerData,
	} = Content_TQC2080_R01();

	const handlePrintByTab = useCallback(() => {
		if (activeTab === "journal") {
			void handlePdfClick();
		} else {
			void handleChartPdfClick();
		}
	}, [activeTab, handlePdfClick, handleChartPdfClick]);

	return (
		<div className="content-container">
			<Actionbar
				onFetchClick={fetchData}
				onCreateClick={handleCreateClick}
				onDeleteClick={handleDeleteClick}
				onSaveClick={handleSaveClick}
				onPrintClick={handlePrintByTab}
				onExcelClick={handleExcelClick}
				onInitClick={initContainerData}
			/>
			<SearchSection_TQC2080_R01 />

			<div className="px-3 pt-2">
				<div
					className="flex items-end mb-[0.1px] bg-white h-[25px]"
					role="tablist"
					aria-label="액분석 일지 탭"
				>
					{MAIN_TABS.map((tab) => {
						const isActive = activeTab === tab.id;
						return (
							<button
								key={tab.id}
								type="button"
								onClick={() => handleTabClick(tab.id)}
								role="tab"
								aria-selected={isActive}
								className={`h-[26px] px-3 text-[12px] font-medium transition-colors border border-gray-300 border-b-0
									${
										isActive
											? "bg-white text-black-700 -mb-px shadow-sm"
											: "bg-gray-100 text-gray-700 hover:bg-gray-200"
									}
								`}
							>
								{tab.label}
							</button>
						);
					})}
				</div>
			</div>

			<div className="flex flex-1 p-3 pt-0 gap-2 min-h-0">
				{activeTab === "journal" ? (
					<div
						ref={journalSplitContainerRef}
						className="flex flex-1 min-h-0 gap-1 w-full"
					>
						<div
							className="h-full min-w-0 flex-shrink-0"
							style={{ width: `${dateListSplitPct}%` }}
						>
							<DateList_TQC2080_R01 onDateRowClick={openDateRowDetail} />
						</div>
						<div
							className="w-[6px] flex-shrink-0 bg-gray-100 cursor-col-resize hover:bg-gray-300 self-stretch"
							onMouseDown={(e) =>
								handleJournalSplitMouseDown("LR", e.nativeEvent)
							}
							role="separator"
							aria-orientation="vertical"
						/>
						<div
							ref={journalTableRef}
							className="h-full min-w-0"
							style={{ width: `${100 - dateListSplitPct}%` }}
						>
							<Table_TQC2080_R01
								gridRef={gridRef}
								analysisDt={TQC2080_R01Filter.searchAnalysisDt}
								onAnalysisDtChange={handleInfoDateChange}
							/>
						</div>
					</div>
				) : (
					<div className="flex-1 h-full">
						<Chart_TQC2080_R01 data={chartData} />
					</div>
				)}
			</div>

			<Message />
		</div>
	);
};
